/*
 * NOTE: We want to maintain this component level template as .ts file so we can add comments and for possibly convert it into
 * a React Component based on usage level for perfomance reasons.
 */

import { ChartType, LegendPlacement } from '../../../src/modules/components/cartesian';

/**
 * This component represents a cartesian chart and contains relevant properties.
 */
export default {
  name: 'dashify:Cartesian',
  title: 'Cartesian',
  owner: 'System',
  solution: '',
  tags: ['data', 'visualization', 'cartesian', 'line', 'area', 'bar', 'column'],
  createdAt: 'May 30, 2023, 10:14 AM',
  updatedAt: 'Oct 19, 2023, 11:48 AM',
  spec: {
    props: {
      // Props directly from Highcharts API
      highchartsConfig: {
        yAxis: {
          type: 'linear',
        },
      },
      // custom props to implement some user functionality that requires multiple Highcharts prop settings
      customConfig: {
        legendPlacement: LegendPlacement.BOTTOM,
        format24Hour: false,
        useDashForNoData: false,
        shouldShowOrphans: true,
      },
    },
    // Highcharts config for legend placement based on Particle
    legendPlacements: {
      bottom: {
        legend: {
          align: 'left',
          layout: 'horizontal',
          verticalAlign: 'bottom',
        },
      },
      right: {
        legend: {
          align: 'right',
          layout: 'vertical',
          verticalAlign: 'middle',
        },
      },
    },
    legendConfig: '/${ $lookup(legendPlacements, props.customConfig.legendPlacement) }',
    timeRange: {
      since: '/${ globals.variables.since }',
      until: "/${ globals.variables.until = 'now' ? undefined : globals.variables.until }",
    },
    series:
      '/${ $reduce(highchartsProps.series, function($acc,$series){ $append($acc, props.customConfig.shouldShowOrphans and $shouldHandleOrphans($series, highchartsProps) ? $handleOrphanDataPoints($transformArrayPoints($series)) : $series) }, [])[] }',
    defaults: {
      chart: {
        type: ChartType.LINE,
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0,0,0,0)',
        spacing: [10, 5, 5, 5],
        style: {
          fontFamily: 'var(--typography-font-body)',
        },
        styledMode: false,
        animation: false,
      },
      colors: [
        'var(--color-accent-a)',
        'var(--color-accent-b)',
        'var(--color-accent-c)',
        'var(--color-accent-d)',
        'var(--color-accent-e)',
        'var(--color-accent-f)',
        'var(--color-accent-g)',
        'var(--color-accent-h)',
        'var(--color-accent-i)',
        'var(--color-accent-j)',
      ],
      xAxis: {
        title: {
          text: '',
        },
        gridLineWidth: 1,
        gridLineColor: 'var(--color-default-border-medium)',
        type: 'datetime',
        dateTimeLabelFormats: {
          minute: "/${ props.customConfig.format24Hour ? '%k:%M' : '%l:%M' }",
          hour: "/${ props.customConfig.format24Hour ? '%k:%M' : '%l:%M' }",
          day: '%m/%d',
          week: '%m/%d',
          month: '%m/%d',
          year: '%b',
        },
        labels: {
          align: 'center',
          distance: 5,
          style: {
            fontFamily: 'var(--typography-font-body)',
            fontSize: '11px',
            fontStyle: 'normal',
            fontWeight: '400',
            color: 'var(--color-default-text-medium)',
            backgroundColor: 'var(--color-default-bg)',
          },
        },
        min: '/${ $getTimeRange(timeRange).since }',
        max: '/${ $getTimeRange(timeRange).until }',
      },
      yAxis: {
        title: {
          text: '',
        },
        gridLineWidth: 1,
        gridLineDashStyle: 'shortdot',
        gridLineColor: 'var(--color-default-border-medium)',
        min: "/${ props.highchartsConfig.yAxis.type = 'logarithmic' ? null : 0 }",
        labels: {
          distance: 5,
          style: {
            fontFamily: 'var(--typography-font-body)',
            fontSize: '11px',
            fontStyle: 'normal',
            fontWeight: '400',
            color: 'var(--color-default-text-medium)',
            backgroundColor: 'var(--color-default-bg)',
          },
        },
      },
      plotOptions: {
        column: {
          borderRadius: 0,
          borderWidth: 0,
        },
        series: {
          marker: {
            enabled: false,
          },
        },
      },
      tooltip: {
        enabled: true,
        useHTML: true,
        outside: true,
        backgroundColor: 'var(--color-default-bg)',
        style: {
          fontFamily: 'var(--typography-font-body)',
          fontSize: '11px',
          fontStyle: 'normal',
          fontWeight: '400',
          color: 'var(--color-default-text)',
          backgroundColor: 'var(--color-default-bg)',
        },
        headerFormat: '<b>{point.key}</b><br>',
        xDateFormat: "/${ props.customConfig.format24Hour ? '%k:%M %p - %m/%d/%Y' : '%l:%M %p - %m/%d/%Y' }",
        valueDecimals: 2,
      },
      legend: {
        enabled: false,
        symbolRadius: 0,
        padding: 0,
        itemStyle: {
          fontFamily: 'var(--typography-font-body)',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '18px',
          color: 'var(--color-default-text-medium)',
          backgroundColor: 'var(--color-default-bg)',
        },
        itemHoverStyle: {
          color: 'var(--color-default-text)',
        },
        navigation: {
          activeColor: 'var(--color-default-text-medium)',
          inactiveColor: 'var(--color-default-text-medium-disabled)',
          arrowSize: 10,
          style: {
            fontFamily: 'var(--typography-font-body)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '400',
            color: 'var(--color-default-text-medium)',
          },
        },
      },
      // Copied from DashHighchart.tsx (to prepare for conversion to React component)
      lang: {
        noData: 'No Data',
      },
      credits: {
        enabled: false,
      },
      title: {
        text: '',
      },
      time: {
        useUTC: false,
      },
      subtitle: {
        text: '',
      },
      accessibility: { enabled: false },
      exporting: {
        enabled: false,
      },
    },
    seriesLength: '/${ $reduce(props.highchartsConfig.series, function ($acc, $val) { $acc + $count($val.data) }, 0) }',
    highchartsProps: '/${ $defaultsDeep({}, legendConfig, props.highchartsConfig, defaults) }',
    view: [
      [
        'dashify:If',
        { condition: '/${ seriesLength > 0 }' },
        [
          [
            'dashify:Then',
            {},
            [
              [
                'dashify:Highcharts',
                "/${ $defaultsDeep({}, $omit(highchartsProps, ['series']), { 'series': series }) }",
              ],
            ],
          ],
          [
            'dashify:Else',
            {},
            [
              [
                'dashify:If',
                { condition: '/${ props.customConfig.useDashForNoData }' },
                [
                  ['dashify:Then', {}, [['p', { className: 'w-[140px]' }, '—']]],
                  ['dashify:Else', {}, [['dashify:EmptyBox']]],
                ],
              ],
            ],
          ],
        ],
      ],
    ],
  },
};
